import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';

// assets
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import { useNavigate } from 'react-router-dom';
import { clearAuth, getUser } from 'utils/auth';

const profileAvatarSx = {
  bgcolor: '#1f5b40',
  color: '#fff'
};

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const navigate = useNavigate();
  const user = getUser();

  const handleLogout = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={{
            p: 0.5,
            borderRadius: 2.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(31, 91, 64, 0.06)',
            },
            '&:focus-visible': {
              outline: `2px solid`,
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              alt="Admin profile"
              size="sm"
              sx={{
                width: 34,
                height: 34,
                border: '2px solid',
                borderColor: open ? 'primary.main' : 'rgba(31, 91, 64, 0.12)',
                transition: 'border-color 0.2s ease',
                ...profileAvatarSx
              }}
            >
              <AdminPanelSettingsRoundedIcon sx={{ fontSize: 19 }} />
            </Avatar>
          </Stack>
        </ButtonBase>
      </Tooltip>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 12]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
                width: 280,
                minWidth: 240,
                maxWidth: { xs: 250, md: 280 },
                borderRadius: 4,
                border: '1px solid rgba(31, 91, 64, 0.06)',
                overflow: 'hidden',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false} sx={{ boxShadow: 'none', border: 'none' }}>
                  <CardContent sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        alt="Admin profile"
                        sx={{
                          width: 44,
                          height: 44,
                          border: '2px solid',
                          borderColor: 'primary.lighter',
                          ...profileAvatarSx
                        }}
                      >
                        <AdminPanelSettingsRoundedIcon sx={{ fontSize: 24 }} />
                      </Avatar>
                      <Stack spacing={0.25} sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                          {user?.name || 'Admin'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user?.email || '—'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>

                  <Divider sx={{ borderColor: 'rgba(31, 91, 64, 0.06)', mx: 2 }} />

                  <Box sx={{ p: 1.5 }}>
                    <ButtonBase
                      onClick={handleLogout}
                      sx={{
                        width: '100%',
                        borderRadius: 2.5,
                        py: 1.25,
                        px: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(239, 68, 68, 0.06)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <LogoutRoundedIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#ef4444' }}>
                          Sign Out
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
